import React from 'react';
import { Card, Row, Col, Container, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Home = () => {
  return (
    <>
      <Helmet>
        <title>Excel Toolbox - Professional Excel File Management Tools</title>
        <meta name="description" content="Free online Excel tools for merging, splitting, converting, and formatting your spreadsheets. No installation required." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section py-5 position-relative">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-3">
                Streamline Excel Tasks
                <br />
                <span className="gradient-text">Directly From Your Browser</span>
              </h1>
              <p className="lead mb-4 text-muted mx-auto" style={{ maxWidth: '600px' }}>
              Transform your Excel workflow with our secure, browser-based tools—experience seamless file management without the hassle.
              </p>
              <div className="d-flex align-items-center gap-3 justify-content-center mb-5">
                <Badge bg="light" className="px-3 py-2 text-success border border-success-subtle">
                  <i className="bi bi-shield-check me-2"></i>
                  Privacy Friendly
                </Badge>
                <Badge bg="light" className="px-3 py-2 text-success border border-success-subtle">
                  <i className="bi bi-lightning me-2"></i>
                  No Installation
                </Badge>
              </div>
              <Row className="g-5 justify-content-center mb-5">
                <Col md={4}>
                  <div className="d-flex flex-column align-items-center">
                    <i className="bi bi-clock fs-1 text-success mb-3"></i>
                    <h3 className="h4 mb-2">Save Hours of Work</h3>
                    <p className="text-muted small">Automate tedious Excel tasks in seconds</p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex flex-column align-items-center">
                    <i className="bi bi-magic fs-1 text-success mb-3"></i>
                    <h3 className="h4 mb-2">No Complex Formulas</h3>
                    <p className="text-muted small">Skip the complicated macros</p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex flex-column align-items-center">
                    <i className="bi bi-graph-up-arrow fs-1 text-success mb-3"></i>
                    <h3 className="h4 mb-2">Boost Productivity</h3>
                    <p className="text-muted small">Wow your colleagues and managers</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section py-5">
        <Container>
          <Row className="g-4">
            <Col md={6} lg={4}>
              <Link to="/excel-deduplication" className="text-decoration-none">
                <Card className="h-100 border-0 shadow-sm hover-card">
                  <Card.Body className="p-4">
                    <div className="text-success mb-3 fs-3">
                      <i className="bi bi-check2-circle"></i>
                    </div>
                    <h3 className="h5 mb-3">Excel Deduplication</h3>
                    <p className="text-muted mb-0">
                      Remove duplicate entries from your Excel files
                    </p>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
            <Col md={6} lg={4}>
              <Link to="/excel-to-json-csv" className="text-decoration-none">
                <Card className="h-100 border-0 shadow-sm hover-card">
                  <Card.Body className="p-4">
                    <div className="text-success mb-3 fs-3">
                      <i className="bi bi-arrow-left-right"></i>
                    </div>
                    <h3 className="h5 mb-3">Excel to JSON/CSV</h3>
                    <p className="text-muted mb-0">
                      Convert Excel files to JSON and CSV
                    </p>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
            <Col md={6} lg={4}>
              <Link to="/text-case-standardizer" className="text-decoration-none">
                <Card className="h-100 border-0 shadow-sm hover-card">
                  <Card.Body className="p-4">
                    <div className="text-success mb-3 fs-3">
                      <i className="bi bi-type"></i>
                    </div>
                    <h3 className="h5 mb-3">Text Case Standardizer</h3>
                    <p className="text-muted mb-0">
                      Standardize text case across your Excel spreadsheets
                    </p>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
            <Col md={6} lg={4}>
              <Link to="/excel-workbook-combiner" className="text-decoration-none">
                <Card className="h-100 border-0 shadow-sm hover-card">
                  <Card.Body className="p-4">
                    <div className="text-success mb-3 fs-3">
                      <i className="bi bi-files"></i>
                    </div>
                    <h3 className="h5 mb-3">Excel Workbook Combiner</h3>
                    <p className="text-muted mb-0">
                      Combine multiple Excel files into one with smart column matching
                    </p>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
            <Col md={6} lg={4}>
              <Link to="/excel-sheet-splitter" className="text-decoration-none">
                <Card className="h-100 border-0 shadow-sm hover-card">
                  <Card.Body className="p-4">
                    <div className="text-success mb-3 fs-3">
                      <i className="bi bi-scissors"></i>
                    </div>
                    <h3 className="h5 mb-3">Excel Sheet Splitter</h3>
                    <p className="text-muted mb-0">
                      Split large Excel files into smaller manageable files
                    </p>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
            <Col md={6} lg={4}>
              <Link to="/suggest-tool" className="text-decoration-none">
                <Card className="h-100 border-0 shadow-sm hover-card">
                  <Card.Body className="p-4">
                    <div className="text-success mb-3 fs-3">
                      <i className="bi bi-lightbulb"></i>
                    </div>
                    <h3 className="h5 mb-3">Suggest a Tool</h3>
                    <p className="text-muted mb-0">
                      Have an Excel challenge? Let us know what tool you need
                    </p>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Home;
